/**
 * AudioPlayer Component
 * Beautiful custom audio player for base64 encoded audio
 */

'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  spacing,
  borders,
  backgroundColors,
  typographyColors,
  shadows,
  typography,
  fontPrimary,
  brandColors,
  statusColors,
  strokeColors,
} from '@/design-system/utils';
import { overlayColors } from '@/design-system/tokens/colors';

export interface AudioPlayerProps {
  audioBase64: string;
  className?: string;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioBase64,
  className = '',
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert base64 to blob URL
  useEffect(() => {
    try {
      const base64Data = audioBase64.includes(',')
        ? audioBase64.split(',')[1]
        : audioBase64;

      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const audioBlob = new Blob([bytes], { type: 'audio/webm' });
      const url = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        // Reset state when loading new audio
        setIsPlaying(false);
        audioRef.current.src = url;
        audioRef.current.load();
      }

      return () => URL.revokeObjectURL(url);
    } catch {
      setError('Failed to load audio');
    }
  }, [audioBase64]);

  // Inject keyframes animation
  useEffect(() => {
    const styleId = 'audio-player-wave-animation';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes audioWave {
        0%, 100% {
          transform: scaleY(1);
        }
        50% {
          transform: scaleY(0.4);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  // Generate animated bars data - memoized to keep consistent
  const bars = useMemo(() => {
    const barCount = 96; // Doubled again for even more wave items
    return Array.from({ length: barCount }, (_, i) => ({
      id: i,
      height: Math.random() * 0.6 + 0.3, // Random height between 0.3 and 0.9
      delay: i * 0.015, // Staggered animation delay (further reduced for smoother wave)
    }));
  }, []);

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing[2],
          padding: spacing[3],
          backgroundColor: statusColors.error.light,
          borderRadius: borders.radius.lg,
          color: statusColors.error.text,
          fontFamily: fontPrimary,
          fontSize: typography.size.sm,
        }}
      >
        <span>⚠️ {error}</span>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        background: backgroundColors.secondary,
        borderRadius: borders.radius.lg,
        padding: `${spacing[3]} ${spacing[4]}`,
        boxShadow: shadows.sm,
        border: `1px solid ${strokeColors.light}`,
        width: '100%',
      }}
    >
      <audio ref={audioRef} />

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          style={{
            flexShrink: 0,
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
            color: typographyColors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: `0 2px 8px ${overlayColors.light}`,
            transition: 'all 0.2s ease',
          }}
          onMouseOver={e => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = `0 4px 12px ${overlayColors.medium}`;
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 2px 8px ${overlayColors.light}`;
          }}
        >
          {isPlaying ? (
            // Pause icon
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill={typographyColors.primary}
            >
              <rect x='6' y='4' width='4' height='16' rx='1' />
              <rect x='14' y='4' width='4' height='16' rx='1' />
            </svg>
          ) : (
            // Play icon
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill={typographyColors.primary}
              style={{ marginLeft: '2px' }}
            >
              <path d='M8 5v14l11-7z' />
            </svg>
          )}
        </button>

        {/* Animated Waveform Bars - Full Width */}
        <div
          style={{
            flex: 1,
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '2px',
            padding: `${spacing[1]} ${spacing[2]}`,
            width: '100%',
          }}
        >
          {bars.map(bar => {
            const minHeight = 4;
            const maxHeight = 28;
            const height = minHeight + (maxHeight - minHeight) * bar.height;

            return (
              <div
                key={bar.id}
                style={{
                  flex: 1,
                  maxWidth: '4px',
                  width: '100%',
                  height: `${height}px`,
                  minHeight: `${minHeight}px`,
                  backgroundColor: isPlaying
                    ? brandColors.primary
                    : strokeColors.medium,
                  borderRadius: '2px',
                  transition: 'background-color 0.3s ease',
                  transformOrigin: 'center bottom',
                  animation: isPlaying
                    ? `audioWave 0.8s ease-in-out ${bar.delay}s infinite`
                    : 'none',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
